/**
 * Système de suggestions de jointure automatique
 * Détecte les connexions entre ressources et propose des ressources intermédiaires appropriées
 */

import { Node, Edge } from 'reactflow';

// Import icon mappings for all providers
import { azureIconsByType } from '../Config/azure-iconsByType';
import { awsIconsByTypeTop100 } from '../Config/aws/aws-iconsByType.top100';
import { gcpIconsByTypeTop100 } from '../Config/gcp/gcp-iconsByType.top100';
import azureDefaultIcon from '../Components/Assets/azure.svg';
import awsDefaultIcon from '../Components/Assets/aws-svgrepo-com.svg';
import gcpDefaultIcon from '../Components/Assets/gcp-svgrepo-com.svg';

export interface ConnectionSuggestion {
  id: string;
  type: 'create_resource' | 'configure_property' | 'warning' | 'info';
  severity: 'critical' | 'recommended' | 'optional';
  title: string;
  description: string;
  resourceType?: string; // Type de ressource à créer (ex: azurerm_private_endpoint)
  resourceName?: string; // Nom suggéré pour la ressource
  properties?: Record<string, any>; // Propriétés pré-remplies
  action?: 'create' | 'configure' | 'none';
  icon?: string;
}

/**
 * Règles de connexion entre ressources
 * Définit quelles ressources intermédiaires sont recommandées ou requises
 */
const CONNECTION_RULES: Record<string, ConnectionSuggestion[]> = {
  // VNet -> Storage Account
  'azurerm_virtual_network->azurerm_storage_account': [
    {
      id: 'vnet-storage-private-endpoint',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Private Endpoint recommandé',
      description: 'Pour une connexion sécurisée entre le VNet et le Storage Account, créez un Private Endpoint',
      resourceType: 'azurerm_private_endpoint',
      resourceName: 'pep-storage',
      properties: {
        name: 'pep-storage',
        service_type: 'blob',
      },
      action: 'create',
      icon: '🔒',
    },
    {
      id: 'vnet-storage-service-endpoint',
      type: 'configure_property',
      severity: 'optional',
      title: 'Service Endpoint (alternative)',
      description: 'Activez un Service Endpoint sur le subnet pour un accès direct au stockage',
      action: 'configure',
      icon: '🔗',
    },
  ],

  // VNet -> SQL Database
  'azurerm_virtual_network->azurerm_sql_database': [
    {
      id: 'vnet-sql-private-endpoint',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Private Endpoint recommandé',
      description: 'Créez un Private Endpoint pour accéder à SQL Database depuis le VNet de manière privée',
      resourceType: 'azurerm_private_endpoint',
      resourceName: 'pep-sql',
      properties: {
        name: 'pep-sql',
        service_type: 'sqlServer',
      },
      action: 'create',
      icon: '🔒',
    },
  ],

  // Subnet -> Virtual Machine
  'azurerm_subnet->azurerm_virtual_machine': [
    {
      id: 'subnet-vm-nic',
      type: 'create_resource',
      severity: 'critical',
      title: 'Network Interface requise',
      description: 'Une VM nécessite une Network Interface pour se connecter au subnet',
      resourceType: 'azurerm_network_interface',
      resourceName: 'nic-vm',
      properties: {
        name: 'nic-vm',
      },
      action: 'create',
      icon: '📡',
    },
    {
      id: 'subnet-vm-nsg',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Network Security Group recommandé',
      description: 'Ajoutez un NSG pour contrôler le trafic vers la VM',
      resourceType: 'azurerm_network_security_group',
      resourceName: 'nsg-vm',
      properties: {
        name: 'nsg-vm',
      },
      action: 'create',
      icon: '🛡️',
    },
  ],

  // Load Balancer -> Virtual Machine
  'azurerm_lb->azurerm_virtual_machine': [
    {
      id: 'lb-vm-backend-pool',
      type: 'configure_property',
      severity: 'critical',
      title: 'Backend Pool requis',
      description: 'Configurez un Backend Pool sur le Load Balancer et associez la VM',
      action: 'configure',
      icon: '⚖️',
    },
    {
      id: 'lb-vm-probe',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Health Probe recommandé',
      description: 'Créez une Health Probe pour vérifier l\'état de la VM',
      resourceType: 'azurerm_lb_probe',
      resourceName: 'probe-http',
      properties: {
        name: 'probe-http',
        protocol: 'Http',
        port: 80,
      },
      action: 'create',
      icon: '🩺',
    },
  ],

  // App Service -> SQL Database
  'azurerm_app_service->azurerm_sql_database': [
    {
      id: 'app-sql-connection-string',
      type: 'configure_property',
      severity: 'critical',
      title: 'Connection String requise',
      description: 'Configurez la connection string dans les paramètres de l\'App Service',
      action: 'configure',
      icon: '🔗',
    },
    {
      id: 'app-sql-managed-identity',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Managed Identity recommandée',
      description: 'Utilisez Managed Identity pour l\'authentification sécurisée sans mot de passe',
      action: 'configure',
      icon: '🆔',
    },
  ],

  // App Service -> Storage Account
  'azurerm_app_service->azurerm_storage_account': [
    {
      id: 'app-storage-connection',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Connection String recommandée',
      description: 'Ajoutez la connection string du Storage Account dans les app settings',
      action: 'configure',
      icon: '🔗',
    },
  ],

  // AKS -> Container Registry
  'azurerm_kubernetes_cluster->azurerm_container_registry': [
    {
      id: 'aks-acr-attach',
      type: 'configure_property',
      severity: 'critical',
      title: 'ACR Attachment requis',
      description: 'Attachez l\'ACR au cluster AKS pour permettre le pull d\'images',
      action: 'configure',
      icon: '📦',
    },
    {
      id: 'aks-acr-rbac',
      type: 'configure_property',
      severity: 'recommended',
      title: 'RBAC recommandé',
      description: 'Configurez les permissions AcrPull pour le service principal AKS',
      action: 'configure',
      icon: '🔐',
    },
  ],

  // App Service -> Key Vault
  'azurerm_app_service->azurerm_key_vault': [
    {
      id: 'app-kv-managed-identity',
      type: 'configure_property',
      severity: 'critical',
      title: 'Managed Identity requise',
      description: 'Activez Managed Identity sur l\'App Service pour accéder au Key Vault',
      action: 'configure',
      icon: '🆔',
    },
    {
      id: 'app-kv-access-policy',
      type: 'configure_property',
      severity: 'critical',
      title: 'Access Policy requise',
      description: 'Créez une Access Policy dans Key Vault pour l\'App Service',
      action: 'configure',
      icon: '🔑',
    },
  ],

  // Virtual Network -> Virtual Network (Peering)
  'azurerm_virtual_network->azurerm_virtual_network': [
    {
      id: 'vnet-vnet-peering',
      type: 'create_resource',
      severity: 'critical',
      title: 'VNet Peering requis',
      description: 'Créez un Virtual Network Peering pour connecter les deux VNets',
      resourceType: 'azurerm_virtual_network_peering',
      resourceName: 'peer-vnets',
      properties: {
        name: 'peer-vnets',
        allow_forwarded_traffic: true,
        allow_gateway_transit: false,
      },
      action: 'create',
      icon: '🔗',
    },
  ],

  // Application Gateway -> App Service
  'azurerm_application_gateway->azurerm_app_service': [
    {
      id: 'appgw-app-backend',
      type: 'configure_property',
      severity: 'critical',
      title: 'Backend Pool requis',
      description: 'Configurez un Backend Pool pointant vers l\'App Service',
      action: 'configure',
      icon: '🌐',
    },
    {
      id: 'appgw-app-probe',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Health Probe recommandée',
      description: 'Créez une Health Probe pour vérifier l\'état de l\'App Service',
      resourceType: 'azurerm_application_gateway_probe',
      resourceName: 'probe-app',
      action: 'create',
      icon: '🩺',
    },
  ],

  // ============================================
  // AWS Connection Rules
  // ============================================

  // VPC -> RDS
  'aws_vpc->aws_db_instance': [
    {
      id: 'vpc-rds-subnet-group',
      type: 'create_resource',
      severity: 'critical',
      title: 'DB Subnet Group requis',
      description: 'Créez un DB Subnet Group pour déployer RDS dans le VPC',
      resourceType: 'aws_db_subnet_group',
      resourceName: 'db-subnet-group',
      properties: {
        name: 'db-subnet-group',
      },
      action: 'create',
      icon: '🗄️',
    },
    {
      id: 'vpc-rds-security-group',
      type: 'create_resource',
      severity: 'critical',
      title: 'Security Group requis',
      description: 'Créez un Security Group pour contrôler l\'accès à la base de données',
      resourceType: 'aws_security_group',
      resourceName: 'sg-rds',
      properties: {
        name: 'sg-rds',
        description: 'Security group for RDS',
      },
      action: 'create',
      icon: '🛡️',
    },
  ],

  // VPC -> S3 (via VPC Endpoint)
  'aws_vpc->aws_s3_bucket': [
    {
      id: 'vpc-s3-endpoint',
      type: 'create_resource',
      severity: 'recommended',
      title: 'VPC Endpoint recommandé',
      description: 'Créez un VPC Endpoint pour accéder à S3 sans passer par Internet',
      resourceType: 'aws_vpc_endpoint',
      resourceName: 'vpce-s3',
      properties: {
        service_name: 'com.amazonaws.region.s3',
        vpc_endpoint_type: 'Gateway',
      },
      action: 'create',
      icon: '🔗',
    },
    {
      id: 'vpc-s3-bucket-policy',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Bucket Policy recommandée',
      description: 'Configurez une Bucket Policy pour limiter l\'accès au VPC',
      action: 'configure',
      icon: '🔐',
    },
  ],

  // ALB -> EC2 Instance
  'aws_lb->aws_instance': [
    {
      id: 'alb-ec2-target-group',
      type: 'create_resource',
      severity: 'critical',
      title: 'Target Group requis',
      description: 'Créez un Target Group et associez l\'instance EC2',
      resourceType: 'aws_lb_target_group',
      resourceName: 'tg-web',
      properties: {
        name: 'tg-web',
        port: 80,
        protocol: 'HTTP',
      },
      action: 'create',
      icon: '🎯',
    },
    {
      id: 'alb-ec2-listener',
      type: 'create_resource',
      severity: 'critical',
      title: 'Listener requis',
      description: 'Créez un Listener sur l\'ALB pour router le trafic',
      resourceType: 'aws_lb_listener',
      resourceName: 'listener-http',
      properties: {
        port: 80,
        protocol: 'HTTP',
      },
      action: 'create',
      icon: '👂',
    },
  ],

  // Lambda -> API Gateway
  'aws_lambda_function->aws_api_gateway_rest_api': [
    {
      id: 'lambda-apigw-integration',
      type: 'configure_property',
      severity: 'critical',
      title: 'Integration requise',
      description: 'Configurez l\'intégration Lambda dans API Gateway',
      action: 'configure',
      icon: '🔌',
    },
    {
      id: 'lambda-apigw-permission',
      type: 'create_resource',
      severity: 'critical',
      title: 'Lambda Permission requise',
      description: 'Autorisez API Gateway à invoquer la fonction Lambda',
      resourceType: 'aws_lambda_permission',
      resourceName: 'lambda-apigw-permission',
      properties: {
        action: 'lambda:InvokeFunction',
        principal: 'apigateway.amazonaws.com',
      },
      action: 'create',
      icon: '🔑',
    },
  ],

  // Lambda -> DynamoDB
  'aws_lambda_function->aws_dynamodb_table': [
    {
      id: 'lambda-dynamodb-iam',
      type: 'configure_property',
      severity: 'critical',
      title: 'IAM Role requis',
      description: 'Ajoutez les permissions DynamoDB au rôle d\'exécution Lambda',
      action: 'configure',
      icon: '🔐',
    },
    {
      id: 'lambda-dynamodb-vpc',
      type: 'info',
      severity: 'optional',
      title: 'VPC Endpoint optionnel',
      description: 'Si Lambda est dans un VPC, créez un VPC Endpoint pour DynamoDB',
      action: 'none',
      icon: 'ℹ️',
    },
  ],

  // ECS -> ECR
  'aws_ecs_cluster->aws_ecr_repository': [
    {
      id: 'ecs-ecr-task-role',
      type: 'configure_property',
      severity: 'critical',
      title: 'Task Execution Role requis',
      description: 'Configurez un Task Execution Role avec permissions ECR',
      action: 'configure',
      icon: '🔐',
    },
    {
      id: 'ecs-ecr-pull-policy',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Pull Policy recommandée',
      description: 'Configurez la politique de pull des images',
      action: 'configure',
      icon: '📦',
    },
  ],

  // EC2 -> EBS Volume
  'aws_instance->aws_ebs_volume': [
    {
      id: 'ec2-ebs-attachment',
      type: 'create_resource',
      severity: 'critical',
      title: 'Volume Attachment requis',
      description: 'Créez un Volume Attachment pour attacher l\'EBS à l\'EC2',
      resourceType: 'aws_volume_attachment',
      resourceName: 'ebs-attachment',
      properties: {
        device_name: '/dev/sdf',
      },
      action: 'create',
      icon: '📎',
    },
  ],

  // VPC -> VPC (Peering)
  'aws_vpc->aws_vpc': [
    {
      id: 'vpc-vpc-peering',
      type: 'create_resource',
      severity: 'critical',
      title: 'VPC Peering Connection requise',
      description: 'Créez une VPC Peering Connection pour connecter les deux VPCs',
      resourceType: 'aws_vpc_peering_connection',
      resourceName: 'vpc-peering',
      properties: {
        name: 'vpc-peering',
      },
      action: 'create',
      icon: '🔗',
    },
    {
      id: 'vpc-vpc-route-tables',
      type: 'configure_property',
      severity: 'critical',
      title: 'Route Tables requises',
      description: 'Mettez à jour les Route Tables des deux VPCs',
      action: 'configure',
      icon: '🗺️',
    },
  ],

  // CloudFront -> S3
  'aws_cloudfront_distribution->aws_s3_bucket': [
    {
      id: 'cloudfront-s3-oai',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Origin Access Identity recommandé',
      description: 'Créez un OAI pour sécuriser l\'accès entre CloudFront et S3',
      resourceType: 'aws_cloudfront_origin_access_identity',
      resourceName: 'oai-s3',
      action: 'create',
      icon: '🔒',
    },
    {
      id: 'cloudfront-s3-bucket-policy',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Bucket Policy recommandée',
      description: 'Configurez la Bucket Policy pour autoriser CloudFront',
      action: 'configure',
      icon: '📋',
    },
  ],

  // EKS -> ECR
  'aws_eks_cluster->aws_ecr_repository': [
    {
      id: 'eks-ecr-iam',
      type: 'configure_property',
      severity: 'critical',
      title: 'IAM Role requis',
      description: 'Configurez les permissions IAM pour que EKS puisse pull depuis ECR',
      action: 'configure',
      icon: '🔐',
    },
  ],

  // ============================================
  // GCP Connection Rules
  // ============================================

  // VPC -> Cloud SQL
  'google_compute_network->google_sql_database_instance': [
    {
      id: 'vpc-sql-private-ip',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Private IP recommandé',
      description: 'Activez Private IP sur Cloud SQL pour une connexion sécurisée via VPC',
      action: 'configure',
      icon: '🔒',
    },
    {
      id: 'vpc-sql-vpc-peering',
      type: 'create_resource',
      severity: 'recommended',
      title: 'VPC Peering recommandé',
      description: 'Créez un VPC Peering pour la connexion privée à Cloud SQL',
      resourceType: 'google_service_networking_connection',
      resourceName: 'vpc-peering-sql',
      action: 'create',
      icon: '🔗',
    },
  ],

  // VPC -> GCS (Cloud Storage)
  'google_compute_network->google_storage_bucket': [
    {
      id: 'vpc-gcs-private-access',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Private Google Access recommandé',
      description: 'Activez Private Google Access sur le subnet pour accéder à GCS',
      action: 'configure',
      icon: '🔒',
    },
  ],

  // GKE -> Artifact Registry
  'google_container_cluster->google_artifact_registry_repository': [
    {
      id: 'gke-ar-iam',
      type: 'configure_property',
      severity: 'critical',
      title: 'IAM Binding requis',
      description: 'Configurez les permissions IAM pour que GKE puisse pull depuis Artifact Registry',
      action: 'configure',
      icon: '🔐',
    },
    {
      id: 'gke-ar-service-account',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Service Account recommandé',
      description: 'Utilisez un Service Account dédié avec le rôle Artifact Registry Reader',
      action: 'configure',
      icon: '👤',
    },
  ],

  // Cloud Run -> Cloud SQL
  'google_cloud_run_service->google_sql_database_instance': [
    {
      id: 'run-sql-vpc-connector',
      type: 'create_resource',
      severity: 'critical',
      title: 'VPC Connector requis',
      description: 'Créez un VPC Access Connector pour que Cloud Run puisse accéder à Cloud SQL',
      resourceType: 'google_vpc_access_connector',
      resourceName: 'vpc-connector',
      action: 'create',
      icon: '🔌',
    },
    {
      id: 'run-sql-connection-string',
      type: 'configure_property',
      severity: 'critical',
      title: 'Connection String requise',
      description: 'Configurez la connection string dans les variables d\'environnement',
      action: 'configure',
      icon: '🔗',
    },
  ],

  // Cloud Function -> Pub/Sub
  'google_cloudfunctions_function->google_pubsub_topic': [
    {
      id: 'function-pubsub-trigger',
      type: 'configure_property',
      severity: 'critical',
      title: 'Trigger requis',
      description: 'Configurez le trigger Pub/Sub dans la Cloud Function',
      action: 'configure',
      icon: '⚡',
    },
    {
      id: 'function-pubsub-iam',
      type: 'configure_property',
      severity: 'recommended',
      title: 'IAM recommandé',
      description: 'Vérifiez que la fonction a les permissions Pub/Sub nécessaires',
      action: 'configure',
      icon: '🔐',
    },
  ],

  // GKE -> GCS
  'google_container_cluster->google_storage_bucket': [
    {
      id: 'gke-gcs-workload-identity',
      type: 'configure_property',
      severity: 'recommended',
      title: 'Workload Identity recommandé',
      description: 'Configurez Workload Identity pour un accès sécurisé à GCS',
      action: 'configure',
      icon: '🆔',
    },
    {
      id: 'gke-gcs-service-account',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Service Account recommandé',
      description: 'Créez un Service Account avec les permissions Storage',
      resourceType: 'google_service_account',
      resourceName: 'sa-gke-storage',
      action: 'create',
      icon: '👤',
    },
  ],

  // Load Balancer -> GCE Instance
  'google_compute_backend_service->google_compute_instance': [
    {
      id: 'lb-gce-instance-group',
      type: 'create_resource',
      severity: 'critical',
      title: 'Instance Group requis',
      description: 'Créez un Instance Group pour associer l\'instance au Load Balancer',
      resourceType: 'google_compute_instance_group',
      resourceName: 'ig-web',
      action: 'create',
      icon: '📦',
    },
    {
      id: 'lb-gce-health-check',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Health Check recommandé',
      description: 'Créez un Health Check pour vérifier l\'état des instances',
      resourceType: 'google_compute_health_check',
      resourceName: 'hc-http',
      properties: {
        check_interval_sec: 5,
      },
      action: 'create',
      icon: '🩺',
    },
  ],

  // Cloud Run -> Artifact Registry
  'google_cloud_run_service->google_artifact_registry_repository': [
    {
      id: 'run-ar-iam',
      type: 'configure_property',
      severity: 'critical',
      title: 'IAM requis',
      description: 'Configurez les permissions pour que Cloud Run puisse pull l\'image',
      action: 'configure',
      icon: '🔐',
    },
  ],

  // VPC -> VPC (Peering)
  'google_compute_network->google_compute_network': [
    {
      id: 'vpc-vpc-peering',
      type: 'create_resource',
      severity: 'critical',
      title: 'VPC Network Peering requis',
      description: 'Créez un VPC Network Peering pour connecter les deux VPCs',
      resourceType: 'google_compute_network_peering',
      resourceName: 'vpc-peering',
      action: 'create',
      icon: '🔗',
    },
    {
      id: 'vpc-vpc-firewall',
      type: 'create_resource',
      severity: 'recommended',
      title: 'Firewall Rules recommandées',
      description: 'Créez des Firewall Rules pour autoriser le trafic entre VPCs',
      resourceType: 'google_compute_firewall',
      resourceName: 'fw-vpc-peering',
      action: 'create',
      icon: '🛡️',
    },
  ],
};

/**
 * Générer une clé de connexion à partir des types de ressources
 */
const getConnectionKey = (sourceType: string, targetType: string): string => {
  return `${sourceType}->${targetType}`;
};

/**
 * Obtenir les suggestions pour une connexion entre deux ressources
 */
export const getConnectionSuggestions = (
  sourceNode: Node,
  targetNode: Node
): ConnectionSuggestion[] => {
  const sourceType = sourceNode.data?.type || sourceNode.data?.tfKey;
  const targetType = targetNode.data?.type || targetNode.data?.tfKey;

  if (!sourceType || !targetType) {
    console.warn('❌ Type de ressource manquant pour la suggestion de connexion');
    return [];
  }

  const connectionKey = getConnectionKey(sourceType, targetType);
  console.log(`🔍 Recherche de règles pour: ${connectionKey}`);

  const suggestions = CONNECTION_RULES[connectionKey];
  if (suggestions) {
    console.log(`✅ ${suggestions.length} règle(s) trouvée(s) pour ${connectionKey}`);
  } else {
    console.log(`ℹ️ Aucune règle définie pour ${connectionKey}`);
  }

  return suggestions || [];
};

/**
 * Vérifier si une connexion nécessite une ressource intermédiaire
 */
export const requiresIntermediateResource = (
  sourceNode: Node,
  targetNode: Node
): boolean => {
  const suggestions = getConnectionSuggestions(sourceNode, targetNode);
  return suggestions.some(
    s => s.type === 'create_resource' && s.severity === 'critical'
  );
};

/**
 * Obtenir la liste des ressources intermédiaires à créer
 */
export const getRequiredIntermediateResources = (
  sourceNode: Node,
  targetNode: Node
): ConnectionSuggestion[] => {
  const suggestions = getConnectionSuggestions(sourceNode, targetNode);
  return suggestions.filter(
    s => s.type === 'create_resource' && s.severity === 'critical'
  );
};

/**
 * Résoudre l'icône d'une ressource selon son type
 */
const resolveIconFromType = (resourceType: string): string => {
  // Azure resources
  if (resourceType.startsWith('azurerm_')) {
    return (azureIconsByType as any)[resourceType] || azureDefaultIcon;
  }

  // AWS resources
  if (resourceType.startsWith('aws_')) {
    return (awsIconsByTypeTop100 as any)[resourceType] || awsDefaultIcon;
  }

  // GCP resources
  if (resourceType.startsWith('google_')) {
    return (gcpIconsByTypeTop100 as any)[resourceType] || gcpDefaultIcon;
  }

  // Fallback to Azure icon if provider unknown
  return azureDefaultIcon;
};

/**
 * Générer automatiquement une ressource intermédiaire
 */
export const generateIntermediateResource = (
  suggestion: ConnectionSuggestion,
  sourceNode: Node,
  targetNode: Node,
  position: { x: number; y: number }
): Node | null => {
  if (!suggestion.resourceType) return null;

  // Resolve proper icon URL from resource type
  const icon = resolveIconFromType(suggestion.resourceType);

  const newNode: Node = {
    id: `${suggestion.resourceType}-${Date.now()}`,
    type: 'custom',
    position,
    data: {
      type: suggestion.resourceType,
      tfKey: suggestion.resourceType,
      label: suggestion.resourceName || suggestion.resourceType,
      icon, // Use resolved icon URL instead of emoji
      params: suggestion.properties || {},
    },
  };

  return newNode;
};

/**
 * Analyser toutes les connexions et retourner les suggestions
 */
export const analyzeAllConnections = (
  nodes: Node[],
  edges: Edge[]
): Map<string, ConnectionSuggestion[]> => {
  const suggestionsByEdge = new Map<string, ConnectionSuggestion[]>();

  edges.forEach(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (sourceNode && targetNode) {
      const suggestions = getConnectionSuggestions(sourceNode, targetNode);
      if (suggestions.length > 0) {
        suggestionsByEdge.set(edge.id, suggestions);
      }
    }
  });

  return suggestionsByEdge;
};

/**
 * Obtenir toutes les suggestions critiques
 */
export const getCriticalSuggestions = (
  nodes: Node[],
  edges: Edge[]
): Array<{ edge: Edge; suggestions: ConnectionSuggestion[] }> => {
  const criticalSuggestions: Array<{ edge: Edge; suggestions: ConnectionSuggestion[] }> = [];

  edges.forEach(edge => {
    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);

    if (sourceNode && targetNode) {
      const suggestions = getConnectionSuggestions(sourceNode, targetNode);
      const critical = suggestions.filter(s => s.severity === 'critical');

      if (critical.length > 0) {
        criticalSuggestions.push({ edge, suggestions: critical });
      }
    }
  });

  return criticalSuggestions;
};
